/**
 * @fileoverview Department authentication method and lifecycle management.
 * @module system-web/components/auth-setup/DepartmentAuthManagementCard
 *
 * @component
 * @localFirst Reads and mutates setup state through useDepartmentAuthSetup.
 * @security Future providers remain unavailable until their release.
 */
import { useState } from 'react';
import { useDepartmentAuthSetup } from '../../hooks/useDepartmentAuthSetup';
import type { DepartmentAuthMethod } from '../../services/departmentAuthService';

const methods: Array<{ id: DepartmentAuthMethod; name: string; detail: string; available: boolean }> = [
  { id: 'local', name: 'Local accounts', detail: 'Email and password managed for this department.', available: true },
  { id: 'microsoft365', name: 'Microsoft 365 / Entra', detail: 'Federated sign-in with first-login provisioning.', available: true },
  { id: 'google', name: 'Google Workspace', detail: 'Federated sign-in for Google-managed identities.', available: false },
  { id: 'saml', name: 'SAML', detail: 'Enterprise SAML identity provider.', available: false },
  { id: 'externalIdp', name: 'Generic OIDC', detail: 'Other OpenID Connect identity provider.', available: false },
];

export interface DepartmentAuthManagementCardProps {
  tenantId: string;
  departmentName?: string;
  onOpenWizard: () => void;
}

export default function DepartmentAuthManagementCard({ tenantId, departmentName, onOpenWizard }: DepartmentAuthManagementCardProps) {
  const { state, stage, isOnline, pending, error, revertToLocal } = useDepartmentAuthSetup(tenantId);
  const [revertOpen, setRevertOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [slug, setSlug] = useState('');
  const currentMethod = state?.authMethod ?? 'local';
  const stagedMethod = state?.provider?.targetAuthMethod as DepartmentAuthMethod | undefined;
  const tenantSlug = state?.tenant?.slug ?? '';
  const expiry = state?.provider?.credentialMetadata?.expiresAt
    ? new Date(state.provider.credentialMetadata.expiresAt)
    : null;
  const expirySoon = expiry !== null && expiry.getTime() <= Date.now() + 30 * 86400000;

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" aria-label="Department authentication">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Authentication method</p>
          <h3 className="mt-1 text-xl font-semibold text-slate-900">{departmentName ?? 'Department'} sign-in</h3>
        </div>
        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${state?.active ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
          {state?.active ? 'Active' : `Staged · ${stage.replace(/_/g, ' ')}`}
        </span>
      </header>

      <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" aria-label="Available authentication methods">
        {methods.map((method) => {
          const selected = currentMethod === method.id;
          const staged = stagedMethod === method.id && !selected;
          return (
            <button key={method.id} type="button" disabled={!method.available}
              aria-pressed={selected || staged}
              className={`min-h-28 rounded-xl border p-4 text-left transition ${selected ? 'border-orange-500 bg-orange-50 ring-1 ring-orange-300' : staged ? 'border-blue-300 bg-blue-50' : method.available ? 'border-slate-200 bg-white hover:border-slate-400' : 'cursor-not-allowed border-slate-200 bg-slate-50 opacity-60'}`}>
              <span className="flex items-center justify-between gap-2">
                <strong className="text-sm text-slate-900">{method.name}</strong>
                <span className="rounded-full bg-white px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                  {selected ? 'Current' : staged ? 'Staged' : method.available ? 'Available' : 'Future release'}
                </span>
              </span>
              <span className="mt-2 block text-xs leading-5 text-slate-600">{method.detail}</span>
            </button>
          );
        })}
      </div>

      <div className="mt-4 rounded-lg bg-slate-50 p-4 text-sm">
        <p><span className="font-semibold text-slate-700">Current sign-in:</span> {methods.find((item) => item.id === currentMethod)?.name}</p>
        <p className="mt-1"><span className="font-semibold text-slate-700">Setup status:</span> {state?.setupStatus?.replace(/_/g, ' ') ?? 'Loading department settings…'}</p>
        {stagedMethod && <p className="mt-1"><span className="font-semibold text-slate-700">Pending method:</span> {methods.find((item) => item.id === stagedMethod)?.name ?? stagedMethod}</p>}
      </div>

      {expiry && <p className={`mt-4 rounded-lg p-3 text-sm ${expirySoon ? 'bg-amber-50 text-amber-900' : 'bg-slate-50 text-slate-700'}`}>
        Client secret expires {expiry.toLocaleDateString()}.
        {expirySoon ? ' Rotate the secret soon to avoid sign-in interruption.' : ' Add a rotation reminder before expiry.'}
      </p>}
      {!isOnline && <p className="mt-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-900">You are offline. Online authentication changes are disabled.</p>}
      {error && <p role="alert" className="mt-3 text-sm text-red-700">{error.message}</p>}

      <div className="mt-5 flex flex-wrap gap-2 border-t border-slate-200 pt-4">
        <button type="button" disabled={!isOnline} onClick={onOpenWizard} className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
          {state?.active ? 'Manage and rotate credentials' : 'Continue setup'}
        </button>
        {currentMethod !== 'local' && <button type="button" disabled={!isOnline || pending} onClick={() => setRevertOpen(true)} className="rounded-lg border border-red-300 px-4 py-2 text-sm font-semibold text-red-700 disabled:opacity-50">
          Revert to local sign-in
        </button>}
      </div>

      {revertOpen && <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-labelledby="auth-revert-title">
        <section className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
          <h4 id="auth-revert-title" className="text-lg font-semibold">Revert to local sign-in?</h4>
          <p className="mt-2 text-sm text-slate-600">Federated sign-in will stop for {departmentName ?? 'this department'}. Existing user identities are retained.</p>
          <label className="mt-4 block text-sm font-medium">Reason for reverting
            <input aria-label="Revert reason" className="mt-1 w-full rounded-lg border p-2" value={reason} onChange={(event) => setReason(event.target.value)} />
          </label>
          <label className="mt-3 block text-sm font-medium">Type the department slug: {tenantSlug}
            <input aria-label="Confirm department slug" className="mt-1 w-full rounded-lg border p-2" value={slug} onChange={(event) => setSlug(event.target.value)} />
          </label>
          <div className="mt-5 flex justify-end gap-2">
            <button type="button" onClick={() => setRevertOpen(false)} className="rounded-lg border px-4 py-2 text-sm">Cancel</button>
            <button type="button" disabled={!reason.trim() || !tenantSlug || slug !== tenantSlug || pending || !isOnline}
              onClick={() => void revertToLocal(reason.trim()).then(() => setRevertOpen(false))}
              className="rounded-lg bg-red-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Confirm revert</button>
          </div>
        </section>
      </div>}
    </section>
  );
}
